import React from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../../../context/AuthContext';
import { updateTask } from '../../../../api/pmsApi';

const customStyles = `
  /* 얇고 예쁜 커스텀 스크롤바 */
  .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: #f1f3f5; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 4px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #adb5bd; }

  /* 툴팁 애니메이션 */
  .gantt-tooltip {
    transition: opacity 0.2s, transform 0.2s;
    pointer-events: none;
    opacity: 0;
    transform: translateY(5px);
  }
  .task-capsule:hover .gantt-tooltip {
    opacity: 1;
    transform: translateY(-50%);
  }

  /* Sticky 컬럼 위로 그림자 (깊이감) */
  .sticky-shadow {
    box-shadow: 4px 0 10px rgba(0,0,0,0.03);
  }

  /* 리사이저 핸들러 디자인 */
  .resizer-handle {
    transition: background-color 0.2s ease;
  }
  .resizer-handle:hover,
  .resizer-handle:active {
    background-color: rgba(13, 110, 253, 0.05) !important;
  }
  .resizer-handle::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 1px;
    background-color: transparent;
    box-shadow: 1px 0 3px rgba(0,0,0,0.05);
  }
  .resizer-handle::after {
    content: '⋮';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: 32px;
    width: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    border: 1px solid #d2d6da;
    border-radius: 20px;
    color: #8c98a4;
    font-size: 14px;
    font-weight: bolder;
    box-shadow: 0 2px 5px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1);
    transition: all 0.2s ease;
    z-index: 10;
  }
  .resizer-handle:hover::after,
  .resizer-handle:active::after {
    border-color: #0d6efd;
    color: #0d6efd;
    box-shadow: 0 3px 8px rgba(13,110,253,0.25), 0 0 2px rgba(13,110,253,0.2);
  }
`;

const InlineConfirmPopup = ({ data, onConfirm, onCancel }) => {
  const popupRef = React.useRef(null);

  // 최초 렌더링 시에도 정확한 넓이(width)를 계산하기 위해 position: fixed 및 디자인 속성을 기본적으로 부여합니다.
  const [style, setStyle] = React.useState({
    position: 'fixed',
    opacity: 0,
    top: -9999,
    left: -9999,
    zIndex: 99999,
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    padding: '12px 14px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.04)',
    minWidth: '150px',
    maxWidth: '200px',
    wordBreak: 'keep-all',
  });

  React.useEffect(() => {
    if (popupRef.current && data) {
      const rect = popupRef.current.getBoundingClientRect();
      const padding = 20;

      // 드래그가 끝난 바로 그 좌표 (마우스 커서 위치 기준)
      let left = data.clientX + 15;
      // 사용자 요청: 세로축 기준으로 막대(마우스)의 정중앙에 오도록 보정
      let top = data.clientY - (rect.height / 2);

      // 화면 오른쪽 경계 충돌 방지
      if (left + rect.width > window.innerWidth - padding) {
        left = data.clientX - rect.width - 15;
      }
      // 화면 하단 경계 충돌 방지
      if (top + rect.height > window.innerHeight - padding) {
        top = window.innerHeight - rect.height - padding;
      }

      // 화면 밖으로 밀려남 방지
      if (left < padding) left = padding;
      if (top < padding) top = padding;

      setStyle(prev => ({
        ...prev,
        left: `${left}px`,
        top: `${top}px`,
        opacity: 1,
        transition: 'opacity 0.2s',
      }));
    } else if (!data) {
      // 팝업이 닫힐 때 초기 상태로 복구
      setStyle(prev => ({ ...prev, opacity: 0, top: -9999, left: -9999, transition: 'none' }));
    }
  }, [data]);

  if (!data) return null;

  return createPortal(
    <div ref={popupRef} style={style} className="animation-fade-in">
      <div className="d-flex flex-column align-items-center">
        <div className="mb-2 fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '12px', letterSpacing: '-0.3px' }}>
          <i className="fas fa-check-circle text-primary" style={{ fontSize: '13px' }}></i>
          변경사항 저장할까요?
        </div>
        <div className="d-flex justify-content-center gap-2 mt-1 w-100">
          <button className="btn btn-light border fw-bolder text-secondary shadow-none" style={{ fontSize: '11px', padding: '3px 14px', borderRadius: '4px' }} onClick={onCancel}>
            취소
          </button>
          <button className="btn btn-primary fw-bolder shadow-none" style={{ fontSize: '11px', padding: '3px 14px', borderRadius: '4px' }} onClick={onConfirm}>
            저장
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

function TimelineTab({ toggleDrawer, tasks: propTasks, categories: propCategories, project: propProject, onDataRefresh, isProjectMaster, currentUser }) {
  // ==========================================
  // [1. 동적 캔버스 및 날짜 엔진 설정]
  // ==========================================
  const { token, user: contextUser } = React.useContext(AuthContext);
  const user = currentUser || contextUser; // props가 없으면 fallback

  const bodyRightRef = React.useRef(null);
  const [rightPanelWidth, setRightPanelWidth] = React.useState(0);

  React.useEffect(() => {
    if (!bodyRightRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        setRightPanelWidth(entry.contentRect.width);
      }
    });
    observer.observe(bodyRightRef.current);
    return () => observer.disconnect();
  }, []);

  // 실데이터에서 전체 날짜 범위를 동적으로 계산 (월 1일 ~ 월 말일 단위로 확장)
  const { projectStart, projectEnd, months } = React.useMemo(() => {
    const allDates = [];
    if (propProject?.start_date) allDates.push(new Date(propProject.start_date));
    if (propProject?.end_date) allDates.push(new Date(propProject.end_date));
    if (propTasks) {
      propTasks.forEach(t => {
        if (t.start_date) allDates.push(new Date(t.start_date));
        if (t.end_date) allDates.push(new Date(t.end_date));
      });
    }
    let pStart, pEnd;
    if (allDates.length === 0) {
      const now = new Date();
      pStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      pStart.setHours(0, 0, 0, 0);
      pEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 10);
      pEnd.setHours(0, 0, 0, 0);
    } else {
      const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
      const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
      pStart = new Date(minDate);
      pStart.setHours(0, 0, 0, 0);
      pStart.setDate(pStart.getDate() - 1);
      pEnd = new Date(maxDate);
      pEnd.setHours(0, 0, 0, 0);
      pEnd.setDate(pEnd.getDate() + 1);
    }
    const monthsArr = [];
    let cur = new Date(pStart);
    while (cur <= pEnd) {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      const startOfMonth = new Date(y, m, 1);
      const endOfMonth = new Date(y, m + 1, 0);
      const effectiveStart = cur > startOfMonth ? cur : startOfMonth;
      const effectiveEnd = pEnd < endOfMonth ? pEnd : endOfMonth;
      const days = Math.round((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1;
      monthsArr.push({ label: `${y}년 ${String(m + 1).padStart(2, '0')}월`, days });
      cur = new Date(y, m + 1, 1);
    }
    return { projectStart: pStart, projectEnd: pEnd, months: monthsArr };
  }, [propProject, propTasks]);

  // === [Today Marker 및 동적 DAY_WIDTH 계산] ===
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isTodayInRange = today >= projectStart && today <= projectEnd;

  const totalDays = Math.floor((projectEnd - projectStart) / (1000 * 60 * 60 * 24)) + 1;
  const MIN_DAY_WIDTH = 32;
  const DAY_WIDTH = rightPanelWidth > 0 ? Math.max(MIN_DAY_WIDTH, rightPanelWidth / totalDays) : MIN_DAY_WIDTH;

  const todayOffset = isTodayInRange ? Math.floor((today - projectStart) / (1000 * 60 * 60 * 24)) * DAY_WIDTH : -1;
  const canvasWidth = totalDays * DAY_WIDTH;

  const daysArray = Array.from({ length: totalDays }).map((_, i) => {
    const d = new Date(projectStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const getPosition = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const offsetDays = Math.floor((s - projectStart) / (1000 * 60 * 60 * 24));
    const durationDays = Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
    return { left: offsetDays * DAY_WIDTH, width: durationDays * DAY_WIDTH };
  };

  // ==========================================
  // [2. 실데이터 변환 (props → 기존 UI 구조)]
  // ==========================================
  const convertedData = React.useMemo(() => {
    if (!propCategories || !propTasks || propCategories.length === 0) return [];

    // 일반 팀원 여부 관련 주석 처리 완료
    // const canEditAnyTask = isProjectMaster || isProjectGeneralMember; // 주석 처리 (사용되지 않음)

    return propCategories
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(cat => {
        const catTasks = propTasks
          .filter(t => String(t.category_id) === String(cat.id))
          .filter(t => t.start_date && t.end_date)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map(t => {
            const taskMembers = t.members || [];
            const assignees = taskMembers.filter(m => m.is_manager === true || m.is_manager === 1);
            let assignee = '';
            if (assignees.length > 0) {
              const mgr = assignees[0];
              const uName = mgr.user?.name || '';
              const pos = mgr.user?.org?.position || '';
              assignee = pos ? `${uName} ${pos}` : uName;
              if (assignees.length > 1) {
                assignee += ` 외 ${assignees.length - 1}명`;
              }
            }

            // 참조자(isPureWatcher): 프로젝트 멤버도 아니고 세부항목 담당자도 아닌 사람
            const isAssignee = assignees.some(a => String(a.user_id) === String(user?.id) || String(a.user_id) === String(user?.userid));
            const isProjectMember = propProject?.members?.some(m =>
              String(m.user_id) === String(user?.id) || String(m.user_id) === String(user?.userid)
            );
            const isPureWatcher = !isProjectMaster && !isProjectMember && !isAssignee;
            // 기간/진척도 드래그 수정 권한: PM이거나 세부항목 실제 담당자만
            const canEditSchedule = isProjectMaster || isAssignee;

            let uiStatus = '시작 전';
            if (t.progress_rate === 100) uiStatus = '완료';
            else if (t.progress_rate > 0) uiStatus = '진행 중';
            return {
              id: `T${t.id}`, dbId: t.id, name: t.title, originalTask: t, isPureWatcher, canEditSchedule,
              start: new Date(t.start_date).toISOString().split('T')[0],
              end: new Date(t.end_date).toISOString().split('T')[0],
              progress: t.progress_rate || 0, assignee, status: uiStatus
            };
          });
        if (catTasks.length === 0) {
          const today = new Date().toISOString().split('T')[0];
          return {
            id: `C${cat.id}`, dbId: cat.id, name: cat.name, type: 'project',
            start: today, end: today,
            progress: 0, tasks: []
          };
        }
        const starts = catTasks.map(t => new Date(t.start));
        const ends = catTasks.map(t => new Date(t.end));
        const catStart = new Date(Math.min(...starts));
        const catEnd = new Date(Math.max(...ends));
        const avgProgress = Math.round(catTasks.reduce((sum, t) => sum + t.progress, 0) / catTasks.length);
        return {
          id: `C${cat.id}`, dbId: cat.id, name: cat.name, type: 'project',
          start: catStart.toISOString().split('T')[0], end: catEnd.toISOString().split('T')[0],
          progress: avgProgress, tasks: catTasks
        };
      })
      .filter(Boolean);
  }, [propCategories, propTasks, propProject, isProjectMaster, user?.id, user?.userid]);


  const getCapsuleColor = (status) => {
    if (status === '완료') return { bg: '#d1e7dd', fill: '#198754' };
    if (status === '진행 중') return { bg: '#cfe2ff', fill: '#0d6efd' };
    return { bg: '#fff3cd', fill: '#ffc107' }; // 시작 전 (노란색/warning)
  };

  const ROW_HEIGHT = 38; //44
  const [listWidth, setListWidth] = React.useState(420);
  const isResizingRef = React.useRef(false);
  const containerRef = React.useRef(null);

  // 스마트 포지셔닝 툴팁 상태 (리사이저에서 쓰이므로 상단으로 이동)
  const [tooltipData, setTooltipData] = React.useState(null);

  // 커스텀 팝업 툴팁 상태
  const [popupData, setPopupData] = React.useState(null);
  const handleMouseEnter = (e, text) => {
    const target = e.currentTarget;

    // 1. 브라우저 Sub-pixel 렌더링 오차(버그) 무시 (말줄임표 없음에도 뜨는 버그 방지)
    const isTextTruncated = target.scrollWidth > target.clientWidth + 2;

    // 2. 패널 리사이즈 시 '실제 텍스트'가 가려지는지 완벽 판별 (Range 객체 사용)
    const leftPane = target.closest('.overflow-x-hidden');
    let isHiddenByPane = false;

    if (leftPane && target.childNodes.length > 0) {
      const paneRect = leftPane.getBoundingClientRect();

      // span 박스 전체가 아닌 '순수 텍스트 영역'만의 정확한 픽셀 좌표 추출
      const range = document.createRange();
      range.selectNodeContents(target);
      const textRect = range.getBoundingClientRect();

      // 실제 텍스트의 우측 끝부분이 패널 바깥으로 삐져나갔는지 검사
      if (textRect.right > paneRect.right + 2) {
        isHiddenByPane = true;
      }
    }

    if (isTextTruncated || isHiddenByPane) {
      const rect = target.getBoundingClientRect();
      setPopupData({ text, top: rect.top, left: rect.left, height: rect.height });
    }
  };
  const handleMouseLeave = () => {
    setPopupData(null);
  };

  const startResizing = React.useCallback((e) => {
    if (tooltipData) setTooltipData(null); // 이동선 클릭 시 툴팁 닫기
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [tooltipData]);

  const stopResizing = React.useCallback(() => {
    if (isResizingRef.current) {
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, []);

  const resize = React.useCallback((e) => {
    if (isResizingRef.current && containerRef.current) {
      const containerLeft = containerRef.current.getBoundingClientRect().left;
      let newWidth = e.clientX - containerLeft;

      // 왼쪽으로 최소 100px 폭 지정, 오른쪽으로 최대 900px 폭 까지만!
      if (newWidth < 100) newWidth = 100;
      if (newWidth > 900) newWidth = 900;

      setListWidth(newWidth);
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  // ==========================================
  // [3. 데이터 상태 및 트리 토글 상태]
  // ==========================================
  // props 실데이터 변환 결과를 로컬 state로 관리 (드래그 앤 드롭 실시간 반영용)
  const [tasksData, setTasksData] = React.useState([]);
  const [expandedProjects, setExpandedProjects] = React.useState({});

  // props 데이터가 변경될 때 로컬 state 동기화
  React.useEffect(() => {
    setTasksData(convertedData);
    setExpandedProjects(prev => {
      const updated = {};
      convertedData.forEach(p => { updated[p.id] = prev[p.id] !== undefined ? prev[p.id] : true; });
      return updated;
    });
  }, [convertedData]);

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const expandAll = () => {
    setExpandedProjects(tasksData.reduce((acc, project) => ({ ...acc, [project.id]: true }), {}));
  };

  const collapseAll = () => {
    setExpandedProjects(tasksData.reduce((acc, project) => ({ ...acc, [project.id]: false }), {}));
  };

  // === [Gantt 드래그 앤 드롭(날짜/진척도 조절) 로직] ===
  const [inlineConfirmData, setInlineConfirmData] = React.useState(null);
  const dragInfo = React.useRef({ type: null, projectId: null, taskId: null, startX: 0, originalStart: null, originalEnd: null, originalProgress: 0, hasChanged: false });
  const clickTimeout = React.useRef(null);

  const handleTaskMouseDown = (e, projectId, taskId, type) => {
    // [중요 방어 로직]: 팝업 대기 중이면 모든 새로운 드래그/클릭 원천 차단
    if (inlineConfirmData) {
      e.stopPropagation();
      return;
    }

    // [추가 방어 로직]: 드래그를 시작할 때, 만약 검은색 정보 레이어(툴팁)가 떠있다면 즉시 닫아줍니다.
    setTooltipData(null);

    e.stopPropagation();
    const task = tasksData.find(p => p.id === projectId).tasks.find(t => t.id === taskId);
    dragInfo.current = {
      type, projectId, taskId, startX: e.clientX,
      originalStart: new Date(task.start), originalEnd: new Date(task.end), originalProgress: task.progress,
      hasChanged: false
    };

    // 마우스 커서 및 텍스트 선택 방지
    document.body.style.userSelect = 'none';
    if (type === 'move') document.body.style.cursor = 'move';
    else if (type === 'resize-right') document.body.style.cursor = 'e-resize';
    else if (type === 'resize-left') document.body.style.cursor = 'w-resize';
    else if (type === 'progress') document.body.style.cursor = 'col-resize';

    window.addEventListener('mousemove', handleTaskMouseMove);
    window.addEventListener('mouseup', handleTaskMouseUp);
  };

  const handleTaskMouseMove = React.useCallback((e) => {
    const info = dragInfo.current;
    if (!info.type) return;

    // 마우스가 이동할 때마다 마지막 좌표를 정확하게 추적
    info.lastX = e.clientX;
    info.lastY = e.clientY;

    const diffX = e.clientX - info.startX;
    const diffDays = Math.round(diffX / DAY_WIDTH); // px를 day 단위로 환산

    // 약간의 픽셀 이동이 감지되었을 때만 변경 플래그를 true로 설정
    if (Math.abs(diffX) > 5) {
      info.hasChanged = true;
    }

    setTasksData(prev => {
      const newData = [...prev];
      const pIndex = newData.findIndex(p => p.id === info.projectId);
      const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };
      const tIndex = project.tasks.findIndex(t => t.id === info.taskId);
      const task = { ...project.tasks[tIndex] };

      if (info.type === 'move') {
        const nStart = new Date(info.originalStart); nStart.setDate(nStart.getDate() + diffDays);
        const nEnd = new Date(info.originalEnd); nEnd.setDate(nEnd.getDate() + diffDays);
        task.start = nStart.toISOString().split('T')[0];
        task.end = nEnd.toISOString().split('T')[0];
      } else if (info.type === 'resize-right') {
        const nEnd = new Date(info.originalEnd); nEnd.setDate(nEnd.getDate() + diffDays);
        if (nEnd >= new Date(task.start)) { // 시작일보다 뒤로 갈 수 없음
          task.end = nEnd.toISOString().split('T')[0];
        }
      } else if (info.type === 'resize-left') {
        const nStart = new Date(info.originalStart); nStart.setDate(nStart.getDate() + diffDays);
        if (nStart <= new Date(task.end)) { // 종료일보다 뒤로 갈 수 없음
          task.start = nStart.toISOString().split('T')[0];
        }
      } else if (info.type === 'progress') {
        const totalDuration = Math.floor((info.originalEnd - info.originalStart) / (1000 * 60 * 60 * 24)) + 1;
        const totalWidth = totalDuration * DAY_WIDTH;
        let newProgress = info.originalProgress + Math.round((diffX / totalWidth) * 100);
        if (newProgress < 0) newProgress = 0;
        if (newProgress > 100) newProgress = 100;
        task.progress = newProgress;

        if (newProgress === 100) task.status = '완료';
        else if (newProgress === 0) task.status = '시작 전';
        else task.status = '진행 중';
      }

      // 마지막으로 계산된 값을 dragInfo에 저장 (비동기 이슈 방지용)
      info.lastCalculated = { start: task.start, end: task.end, progress: task.progress, name: task.name };

      project.tasks[tIndex] = task;
      newData[pIndex] = project;
      return newData;
    });
  }, [DAY_WIDTH]);

  const handleTaskMouseUp = React.useCallback((e) => {
    const info = dragInfo.current;

    if (info.hasChanged && info.type && info.lastCalculated) {
      const oldStart = info.originalStart.toISOString().split('T')[0];
      const oldEnd = info.originalEnd.toISOString().split('T')[0];
      const cur = info.lastCalculated;

      let isActuallyChanged = false;
      if (info.type === 'progress') {
        if (cur.progress !== info.originalProgress) isActuallyChanged = true;
      } else {
        if (cur.start !== oldStart || cur.end !== oldEnd) isActuallyChanged = true;
      }

      if (isActuallyChanged) {
        // --- [프로젝트 전체 일정 초과 방지 로직 (Block)] ---
        const projStartStr = propProject?.start_date;
        const projEndStr = propProject?.end_date;

        let isExceeding = false;
        if (info.type === 'move' || info.type === 'resize-right' || info.type === 'resize-left') {
          if (projStartStr && cur.start < projStartStr) isExceeding = true;
          if (projEndStr && cur.end > projEndStr) isExceeding = true;
        }

        if (isExceeding) {
          // 화면에 즉시 원위치 롤백 반영
          setTasksData(prev => {
            const newData = [...prev];
            const pIndex = newData.findIndex(p => p.id === info.projectId);
            if (pIndex > -1) {
              const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };
              const tIndex = project.tasks.findIndex(t => t.id === info.taskId);
              if (tIndex > -1) {
                const currentTask = { ...project.tasks[tIndex] };
                currentTask.start = oldStart;
                currentTask.end = oldEnd;
                currentTask.progress = info.originalProgress;
                if (currentTask.progress === 100) currentTask.status = '완료';
                else if (currentTask.progress === 0) currentTask.status = '시작 전';
                else currentTask.status = '진행 중';
                project.tasks[tIndex] = currentTask;
                newData[pIndex] = project;
              }
            }
            return newData;
          });

          alert(`일정 변경 불가\n\n해당 세부항목의 일정을 변경할 수 없습니다.\n프로젝트 전체 지정 기간(${projStartStr} ~ ${projEndStr})을 초과하는 일정은 설정할 수 없습니다.`);

          // 드래그 상태 초기화
          document.body.style.userSelect = '';
          document.body.style.cursor = '';
          window.removeEventListener('mousemove', handleTaskMouseMove);
          window.removeEventListener('mouseup', handleTaskMouseUp);
          setTimeout(() => {
            dragInfo.current.type = null;
            dragInfo.current.lastCalculated = null;
            dragInfo.current.hasChanged = false;
          }, 50);
          return; // 여기서 로직 종료 (업데이트 안 함)
        }
        // --- [초과 방지 로직 끝] ---

        // 인라인 팝업 띄움 (마지막 마우스 좌표 사용)
        const clientX = info.lastX || window.innerWidth / 2;
        const clientY = info.lastY || window.innerHeight / 2;

        setInlineConfirmData({
          type: info.type,
          originalStart: oldStart,
          originalEnd: oldEnd,
          originalProgress: info.originalProgress,
          newStart: cur.start,
          newEnd: cur.end,
          newProgress: cur.progress,
          projectId: info.projectId,
          taskId: info.taskId,
          clientX,
          clientY
        });
      } else {
        // 변화가 미미하면 즉시 원위치로 롤백 (스냅백 효과)
        setTasksData(prev => {
          const newData = [...prev];
          const pIndex = newData.findIndex(p => p.id === info.projectId);
          if (pIndex > -1) {
            const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };
            const tIndex = project.tasks.findIndex(t => t.id === info.taskId);
            if (tIndex > -1) {
              const currentTask = { ...project.tasks[tIndex] };
              currentTask.start = oldStart;
              currentTask.end = oldEnd;
              currentTask.progress = info.originalProgress;

              if (currentTask.progress === 100) currentTask.status = '완료';
              else if (currentTask.progress === 0) currentTask.status = '시작 전';
              else currentTask.status = '진행 중';

              project.tasks[tIndex] = currentTask;
              newData[pIndex] = project;
            }
          }
          return newData;
        });
      }
    }

    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    window.removeEventListener('mousemove', handleTaskMouseMove);
    window.removeEventListener('mouseup', handleTaskMouseUp);

    // click 이벤트가 처리된 이후에 상태가 초기화되도록 약간 지연시킴
    setTimeout(() => {
      dragInfo.current.type = null;
      dragInfo.current.lastCalculated = null;
      dragInfo.current.hasChanged = false;
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleTaskMouseMove, token, onDataRefresh, propProject?.end_date, propProject?.start_date]);

  const handleInlineConfirmAction = async () => {
    if (!inlineConfirmData) return;
    const dbTaskId = inlineConfirmData.taskId.replace('T', '');
    try {
      const updateData = {};
      if (inlineConfirmData.type === 'progress') {
        updateData.progress_rate = inlineConfirmData.newProgress;
        if (inlineConfirmData.newProgress === 100) updateData.status = 'completed';
        else if (inlineConfirmData.newProgress === 0) updateData.status = 'pending';
        else updateData.status = 'in_progress';
      } else {
        updateData.start_date = inlineConfirmData.newStart;
        updateData.end_date = inlineConfirmData.newEnd;
      }
      await updateTask(dbTaskId, updateData, token);
      setInlineConfirmData(null);
      if (onDataRefresh) onDataRefresh(false);
    } catch (err) {
      console.error('타임라인 업데이트 실패:', err);
      alert('변경 사항 저장에 실패했습니다.');
      setTasksData(prev => {
        const newData = [...prev];
        const pIndex = newData.findIndex(p => p.id === inlineConfirmData.projectId);
        if (pIndex > -1) {
          const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };
          const tIndex = project.tasks.findIndex(t => t.id === inlineConfirmData.taskId);
          if (tIndex > -1) {
            const task = { ...project.tasks[tIndex] };
            task.start = inlineConfirmData.originalStart;
            task.end = inlineConfirmData.originalEnd;
            task.progress = inlineConfirmData.originalProgress;
            if (task.progress === 100) task.status = '완료';
            else if (task.progress === 0) task.status = '시작 전';
            else task.status = '진행 중';
            project.tasks[tIndex] = task;
            newData[pIndex] = project;
          }
        }
        return newData;
      });
      setInlineConfirmData(null);
    }
  };

  const handleInlineCancelAction = () => {
    if (!inlineConfirmData) return;
    setTasksData(prev => {
      const newData = [...prev];
      const pIndex = newData.findIndex(p => p.id === inlineConfirmData.projectId);
      if (pIndex > -1) {
        const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };
        const tIndex = project.tasks.findIndex(t => t.id === inlineConfirmData.taskId);
        if (tIndex > -1) {
          const task = { ...project.tasks[tIndex] };
          task.start = inlineConfirmData.originalStart;
          task.end = inlineConfirmData.originalEnd;
          task.progress = inlineConfirmData.originalProgress;
          if (task.progress === 100) task.status = '완료';
          else if (task.progress === 0) task.status = '시작 전';
          else task.status = '진행 중';
          project.tasks[tIndex] = task;
          newData[pIndex] = project;
        }
      }
      return newData;
    });
    setInlineConfirmData(null);
  };

  // ==========================================
  // [4. 4분할 스크롤 동기화 로직 및 오버레이 버튼 상태]
  // ==========================================
  const headerRightRef = React.useRef(null);
  const bodyLeftRef = React.useRef(null);
  // bodyRightRef는 최상단으로 이동됨 (ResizeObserver용)

  // 스크롤 양끝 감지 상태 (좌/우 화살표 자동 숨김용)
  const [isScrollLeftEnd, setIsScrollLeftEnd] = React.useState(true);
  const [isScrollRightEnd, setIsScrollRightEnd] = React.useState(true); // 초기에 화살표를 일단 숨기고, useEffect로 너비 검사 후 노출
  const [showTodayButton, setShowTodayButton] = React.useState(false);

  // 컨텐츠 길이에 따라 스크롤 화살표 표시 여부 초기 검사
  React.useEffect(() => {
    if (bodyRightRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = bodyRightRef.current;
      setIsScrollLeftEnd(scrollLeft <= 1);
      setIsScrollRightEnd(scrollLeft + clientWidth >= scrollWidth - 1);
    }
  }, [canvasWidth, expandedProjects, tasksData]);

  // 자동 스크롤 중 양방향 동기화 이벤트 충돌 차단용 플래그
  const isAutoScrollingRef = React.useRef(false);

  // 캡슐 좌/우 가려짐 화살표 표시 업데이트 함수
  const updateCapsuleArrows = React.useCallback(() => {
    if (!bodyRightRef.current) return;
    const { scrollLeft, clientWidth } = bodyRightRef.current;
    const rightPanelLeft = scrollLeft;
    const rightPanelRight = scrollLeft + clientWidth;

    // RequestAnimationFrame을 통해 부드러운 DOM 업데이트 처리
    requestAnimationFrame(() => {
      const capsules = document.querySelectorAll('.task-capsule');
      capsules.forEach(capsule => {
        const left = parseFloat(capsule.getAttribute('data-left'));
        const width = parseFloat(capsule.getAttribute('data-width'));
        if (isNaN(left) || isNaN(width)) return;

        const right = left + width;
        const row = capsule.closest('.task-row');
        if (!row) return;

        const leftArrow = row.querySelector('.left-arrow');
        const rightArrow = row.querySelector('.right-arrow');

        // 왼쪽 끝이 화면 왼쪽(과거)으로 넘어갔을 때 (완전히 숨든 걸치든 무조건)
        if (leftArrow) {
          if (left < rightPanelLeft) {
            leftArrow.style.display = 'flex';
            leftArrow.style.transform = `translateX(${rightPanelLeft + 3}px)`;
          } else {
            leftArrow.style.display = 'none';
          }
        }

        // 오른쪽 끝이 화면 오른쪽(미래)으로 넘어갔을 때 (완전히 숨든 걸치든 무조건)
        if (rightArrow) {
          if (right > rightPanelRight) {
            rightArrow.style.display = 'flex';
            rightArrow.style.transform = `translateX(${rightPanelRight - 21}px)`;
          } else {
            rightArrow.style.display = 'none';
          }
        }
      });
    });
  }, []);

  // 데이터나 컨버팅 결과가 바뀌어 다시 렌더링될 때도 화살표 업데이트 수행
  // 브라우저 크기를 줄이거나 늘릴 때도 즉각 위치를 보정하기 위해 resize 이벤트 추가
  React.useEffect(() => {
    updateCapsuleArrows();
    window.addEventListener('resize', updateCapsuleArrows);
    return () => window.removeEventListener('resize', updateCapsuleArrows);
  }, [tasksData, expandedProjects, canvasWidth, updateCapsuleArrows]);

  const handleBodyRightScroll = (e) => {
    const { scrollLeft, scrollTop, scrollWidth, clientWidth } = e.target;

    if (tooltipData) setTooltipData(null); // 스크롤 시 툴팁 숨김

    // 자동 스크롤 중에는 좌측 패널 세로 동기화 생략 (smooth 애니메이션 보호)
    if (!isAutoScrollingRef.current) {
      if (headerRightRef.current) headerRightRef.current.scrollLeft = scrollLeft;
      if (bodyLeftRef.current) bodyLeftRef.current.scrollTop = scrollTop;
    } else {
      // 가로 헤더 동기화는 자동 스크롤 중에도 유지
      if (headerRightRef.current) headerRightRef.current.scrollLeft = scrollLeft;
    }

    // 가로 스크롤 끝 감지 (오차 보정을 위해 1px 정도 여유를 둠)
    setIsScrollLeftEnd(scrollLeft <= 1);
    setIsScrollRightEnd(scrollLeft + clientWidth >= scrollWidth - 1);

    // 가려짐 화살표 업데이트 실행
    updateCapsuleArrows();

    // [오늘 날짜 노출 여부 계산] 오늘 점선이 화면을 벗어났을 때만 버튼 표시
    if (isTodayInRange) {
      const isVisible = todayOffset >= scrollLeft - DAY_WIDTH && todayOffset <= scrollLeft + clientWidth;
      setShowTodayButton(!isVisible);
    }
  };

  const handleBodyLeftScroll = (e) => {
    // 자동 스크롤 중에는 우측 패널 덮어쓰기 방지 (smooth 애니메이션 보호)
    if (isAutoScrollingRef.current) return;
    if (bodyRightRef.current) bodyRightRef.current.scrollTop = e.target.scrollTop;
  };

  // 화살표 버튼 클릭 시 스크롤 부드럽게 이동 액션
  const SCROLL_AMOUNT = 400; // 한 번 클릭 시 이동할 픽셀
  const scrollLeftAction = () => {
    if (bodyRightRef.current) bodyRightRef.current.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };
  const scrollRightAction = () => {
    if (bodyRightRef.current) bodyRightRef.current.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  // 오늘 날짜로 캔버스 스크롤 (화면 정중앙 포커싱)
  const goToToday = React.useCallback(() => {
    if (bodyRightRef.current && isTodayInRange) {
      const clientWidth = bodyRightRef.current.clientWidth;
      bodyRightRef.current.scrollTo({
        left: todayOffset - (clientWidth / 2) + (DAY_WIDTH / 2),
        behavior: 'smooth'
      });
    }
  }, [isTodayInRange, todayOffset, DAY_WIDTH]);

  // 최초 1회에 한해서만 오늘 날짜로 자동 포커싱
  const hasScrolledRef = React.useRef(false);

  React.useEffect(() => {
    if (hasScrolledRef.current) return;
    if (!tasksData || tasksData.length === 0) return; // 데이터 로딩 대기
    if (!expandedProjects || Object.keys(expandedProjects).length === 0) return; // 폴더 상태 초기화 대기

    const timer = setTimeout(() => {
      if (bodyRightRef.current && isTodayInRange) {
        const clientWidth = bodyRightRef.current.clientWidth;
        const clientHeight = bodyRightRef.current.clientHeight;

        let targetY;
        const todayDate = new Date();
        const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
        let currentRow = 0;

        for (const project of tasksData) {
          currentRow++; // 프로젝트 헤더
          if (expandedProjects[project.id] && project.tasks) {
            for (const task of project.tasks) {
              if (task.start <= todayStr && task.end >= todayStr) {
                targetY = currentRow * ROW_HEIGHT;
                break;
              }
              currentRow++;
            }
          }
          if (targetY !== undefined) break;
        }

        const scrollOptions = {
          left: Math.max(0, todayOffset - (clientWidth / 2) + (DAY_WIDTH / 2)),
          behavior: 'smooth'
        };

        if (targetY !== undefined) {
          scrollOptions.top = Math.max(0, targetY - (clientHeight / 2) + (ROW_HEIGHT / 2));
        }

        // 자동 스크롤 시작 전 차단기 ON → smooth 애니메이션 동안 onScroll 충돌 방지
        isAutoScrollingRef.current = true;
        bodyRightRef.current.scrollTo(scrollOptions);

        // smooth 스크롤 완료 예상 시간(800ms) 이후 차단기 해제
        setTimeout(() => {
          isAutoScrollingRef.current = false;
          // 좌측 패널 세로 위치 수동 동기화 (차단기 해제 후 한 번만)
          if (bodyLeftRef.current && bodyRightRef.current) {
            bodyLeftRef.current.scrollTop = bodyRightRef.current.scrollTop;
          }
        }, 800);

        hasScrolledRef.current = true;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isTodayInRange, todayOffset, DAY_WIDTH, tasksData, expandedProjects]);


  return (
    <div
      className="bg-white rounded shadow-sm border d-flex flex-column animation-fade-in"
      style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, overflow: 'hidden' }}
      onClick={() => { if (tooltipData) setTooltipData(null); }}
    >
      <style>{customStyles}</style>
      <style>{`
        /* 좌측 바디의 보기 흉한 스크롤바를 숨겨서 우측과 디자인 맞춤 */
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        /* 툴팁 가림 가림 방지: 호버된 행(Row)의 z-index를 최상단으로 끌어올림 */
        .task-row:hover { z-index: 50 !important; }
        /* 태스크 이름 클릭 가능 표시 (밑줄 제거, 색상 변경만) */
        .task-name-hover:hover { color: #4c6ef5 !important; }
      `}</style>

      {/* 상단 탭과의 간격을 위한 여백 공간 (기존 타이틀 영역 대체) */}
      <div style={{ height: '16px', flexShrink: 0, backgroundColor: 'transparent' }}></div>

      {/* 컨텐츠 래퍼 (4분할 레이아웃 적용) */}
      <div className="flex-grow-1 d-flex flex-column position-relative" ref={containerRef} style={{ minHeight: 0 }}>
        {/* 통짜 리사이저 핸들러 (헤더와 바디를 아우르는 단일 선) */}
        <div className="resizer-handle" onMouseDown={startResizing} title="드래그하여 크기 조절" style={{ width: '8px', position: 'absolute', left: `${listWidth - 4}px`, top: 0, bottom: 0, zIndex: 100, background: 'transparent', cursor: 'col-resize' }} />

        {/* ================================================= */}
        {/* [상단 헤더 래퍼] (세로 스크롤 없이 항상 100% 고정) */}
        {/* ================================================= */}
        <div className="d-flex border-top border-bottom border-secondary border-opacity-25 bg-white" style={{ height: '60px', flexShrink: 0, zIndex: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>

          {/* 1. 좌측 헤더 (고정 폭 -> 가변 폭 덮어쓰기) */}
          <div className="position-relative border-end border-secondary border-opacity-25 overflow-hidden" style={{ width: `${listWidth}px`, flexShrink: 0, boxSizing: 'border-box' }}>
            <div className="d-flex align-items-center bg-light h-100" style={{ width: `${Math.max(420, listWidth)}px` }}>
              <div className="ps-3 pe-2 fw-bolder text-secondary fs-8 d-flex justify-content-between align-items-center" style={{ width: 'calc(100% - 214px)' }}>
                <span>업무명</span>
                <div className="d-flex gap-1">
                  <div className="d-flex align-items-center justify-content-center bg-white border rounded cursor-pointer hover-bg-light" onClick={expandAll} title="전체 펼치기" style={{ width: '20px', height: '20px', cursor: 'pointer' }}>
                    <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                  </div>
                  <div className="d-flex align-items-center justify-content-center bg-white border rounded cursor-pointer hover-bg-light" onClick={collapseAll} title="전체 접기" style={{ width: '20px', height: '20px', cursor: 'pointer' }}>
                    <i className="fas fa-minus" style={{ fontSize: '10px' }}></i>
                  </div>
                </div>
              </div>
              <div className="px-2 text-center border-start border-end border-secondary border-opacity-10 fw-bolder text-secondary fs-8" style={{ width: '151px' }}>진행 기간</div>
              <div className="px-2 text-center fw-bolder text-secondary fs-8" style={{ width: '63px' }}>진척도</div>
            </div>
          </div>

          {/* 2. 우측 달력 헤더 래퍼 (스크롤 영역과 고정 오버레이 버튼을 분리) */}
          <div className="flex-grow-1 position-relative d-flex" style={{ minWidth: 0 }}>
            {/* 실제 가로 스크롤바가 숨겨지고 스크롤되는 영역 */}
            <div className="flex-grow-1 overflow-hidden" ref={headerRightRef} style={{ minWidth: 0 }}>
              <div className="d-flex">
                <div style={{ width: `${canvasWidth}px`, flexShrink: 0 }}>
                  <div className="d-flex bg-light border-bottom border-secondary border-opacity-10" style={{ height: '26px' }}>
                    {months.map((m, idx) => (
                      <div key={idx} className="d-flex align-items-center justify-content-center fw-bolder text-secondary fs-8 border-end border-secondary border-opacity-25" style={{ width: `${m.days * DAY_WIDTH}px`, boxSizing: 'border-box' }}>
                        {m.label}
                      </div>
                    ))}
                  </div>
                  <div className="d-flex bg-white" style={{ height: '34px' }}>
                    {daysArray.map((day, idx) => {
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      const isLast = idx === daysArray.length - 1;
                      const isBoundary = isLast || (idx < daysArray.length - 1 && daysArray[idx + 1].getDate() === 1);
                      const isToday = isTodayInRange && day.getTime() === today.getTime();

                      return (
                        <div key={idx} className={`d-flex flex-column align-items-center justify-content-center border-end border-secondary ${isBoundary ? 'border-opacity-50' : 'border-opacity-10'}`}
                          style={{ width: `${DAY_WIDTH}px`, backgroundColor: isWeekend ? '#f8f9fa' : '#fff', boxSizing: 'border-box' }}>
                          {isToday ? (
                            <span className="badge rounded-circle text-white d-flex align-items-center justify-content-center p-0 shadow-sm" style={{ backgroundColor: '#fd7e14', width: '22px', height: '22px', fontSize: '11px', transform: 'translateY(-1px)' }}>
                              {String(day.getDate()).padStart(2, '0')}
                            </span>
                          ) : (
                            <span className="fs-8 fw-medium" style={{ color: day.getDay() === 0 ? '#dc3545' : day.getDay() === 6 ? '#0d6efd' : '#868e96' }}>
                              {String(day.getDate()).padStart(2, '0')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* 바디 세로스크롤바 두께(8px)를 보정하는 공간 - 엑셀 스타일 '코너 블록'으로 고급스럽게 디자인 마감 (테두리 없음, 1px 유지) */}
                <div className="d-flex flex-column bg-light" style={{ width: '8px', flexShrink: 0 }}>
                  {/* 상단 '월' 헤더와 이어지는 26px 구분선 */}
                  <div className="border-bottom border-secondary border-opacity-10" style={{ height: '26px' }}></div>
                  {/* 하단 '일' 헤더와 이어지는 34px 코너 배경 */}
                  <div style={{ height: '34px' }}></div>
                </div>
              </div>
            </div>

            {/* ★ 좌우 스크롤 이동 오버레이 화살표 버튼 (고정된 래퍼에 떠 있으므로 스크롤에 쓸려가지 않음) ★ */}
            {convertedData.length > 0 && !isScrollLeftEnd && (
              <div
                className="position-absolute d-flex align-items-center justify-content-center shadow-sm cursor-pointer transition-all"
                onClick={scrollLeftAction}
                style={{
                  left: '10px', top: 'calc(50% - 5px)', transform: 'translateY(-50%)',
                  width: '32px', height: '32px', borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(4px)', border: '1px solid rgba(13, 110, 253, 0.15)', zIndex: 50, cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'; e.currentTarget.style.borderColor = 'rgba(13, 110, 253, 0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)'; e.currentTarget.style.borderColor = 'rgba(13, 110, 253, 0.15)'; }}
                title="이전으로 스크롤"
              >
                <i className="fas fa-chevron-left text-primary fs-6 opacity-50"></i>
              </div>
            )}

            {convertedData.length > 0 && !isScrollRightEnd && (
              <div
                className="position-absolute d-flex align-items-center justify-content-center shadow-sm cursor-pointer transition-all"
                onClick={scrollRightAction}
                style={{
                  right: '15px', top: 'calc(50% - 5px)', transform: 'translateY(-50%)',
                  width: '32px', height: '32px', borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(4px)', border: '1px solid rgba(13, 110, 253, 0.15)', zIndex: 50, cursor: 'pointer'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'; e.currentTarget.style.borderColor = 'rgba(13, 110, 253, 0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)'; e.currentTarget.style.borderColor = 'rgba(13, 110, 253, 0.15)'; }}
                title="다음으로 스크롤"
              >
                <i className="fas fa-chevron-right text-primary fs-6 opacity-50"></i>
              </div>
            )}
          </div>
        </div>

        {/* ================================================= */}
        {/* [하단 바디 래퍼] (세로 스크롤은 여기서부터 시작됨) */}
        {/* ================================================= */}
        <div className="flex-grow-1 d-flex position-relative" style={{ minHeight: 0 }}>

          {/* 3. 좌측 바디 (세로 스크롤바는 숨기되 휠 작동 시 우측과 동기화됨) */}
          <div className="position-relative overflow-y-auto overflow-x-hidden hide-scrollbar border-end border-secondary border-opacity-25" ref={bodyLeftRef} onScroll={handleBodyLeftScroll} style={{ width: `${listWidth}px`, flexShrink: 0 }}>
            <div className="pb-2">
              {tasksData.map((project, index) => (
                <React.Fragment key={project.id}>
                  {/* 프로젝트 Row */}
                  <div className={`d-flex align-items-center bg-light bg-opacity-50 border-bottom hover-bg-light ${index !== 0 ? 'border-top' : ''}`} onClick={() => toggleProject(project.id)} style={{ width: `${Math.max(420, listWidth)}px`, height: `${ROW_HEIGHT}px`, boxSizing: 'border-box', borderColor: 'rgba(108, 117, 125, 0.2)', cursor: 'pointer' }}>
                    <div style={{ width: 'calc(100% - 214px)', position: 'relative' }}>
                      <div className="ps-2 fw-bold text-dark text-truncate d-flex align-items-center gap-2" style={{ fontSize: '13px', width: '100%' }}>
                        {project.tasks && project.tasks.length > 0 ? (
                          <i className={`fas fa-chevron-${expandedProjects[project.id] ? 'down' : 'right'} text-secondary opacity-75 fs-9 text-center`} style={{ width: '14px', flexShrink: 0 }}></i>
                        ) : (
                          <span style={{ width: '14px', flexShrink: 0 }}></span>
                        )}
                        <span
                          className="text-truncate"
                          onMouseEnter={(e) => handleMouseEnter(e, project.name)}
                          onMouseLeave={handleMouseLeave}
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          {project.name}
                        </span>
                      </div>
                    </div>
                    <div className="text-center text-secondary fw-medium" style={{ width: '151px', fontSize: '11px' }}>{project.start} ~ {project.end}</div>
                    <div className="text-center" style={{ width: '63px' }}>
                      <span className="badge border bg-secondary bg-opacity-10 text-secondary border-secondary border-opacity-50">
                        {project.progress}%
                      </span>
                    </div>
                  </div>
                  {/* 태스크 Row */}
                  {expandedProjects[project.id] && project.tasks.map(task => {
                    // [좌측 리스트 연동: 일정 지연 판별 로직 (글로벌 스탠다드)]
                    // 마감일이 어제로 지났는데도 100%가 아니면 지연(빨간색), 그 외는 무조건 정상(파란색)
                    const tEnd = new Date(task.end);
                    const isDelayed = today > tEnd && task.progress < 100;

                    // [툴팁 상태명 판별]
                    let badgeTooltip = "진행 중";
                    if (task.progress === 100) {
                      badgeTooltip = "완료";
                    } else if (task.progress === 0) {
                      badgeTooltip = "시작 전";
                    } else if (isDelayed) {
                      badgeTooltip = "미완료 (일정 지연)";
                    }

                    return (
                      <div key={task.id} className="d-flex align-items-center bg-white border-bottom border-secondary border-opacity-10 hover-bg-light" style={{ width: `${Math.max(420, listWidth)}px`, height: `${ROW_HEIGHT}px`, boxSizing: 'border-box' }}>
                        <div style={{ width: 'calc(100% - 214px)', position: 'relative' }}>
                          <div className="ps-4 pe-2 text-truncate d-flex align-items-center gap-2" style={{ fontSize: '12px', color: '#495057', width: '100%' }}>
                            <i className="fas fa-level-up-alt text-secondary opacity-25" style={{ transform: 'rotate(90deg)', flexShrink: 0 }}></i>
                            <span
                              className={`text-truncate ${!task.isPureWatcher && toggleDrawer ? 'task-name-hover' : ''}`}
                              onMouseEnter={(e) => handleMouseEnter(e, task.name)}
                              onMouseLeave={handleMouseLeave}
                              onClick={(e) => {
                                if (!task.isPureWatcher && toggleDrawer) {
                                  e.stopPropagation();
                                  toggleDrawer(task.originalTask);
                                }
                              }}
                              style={{
                                flex: 1, minWidth: 0, transition: 'color 0.15s',
                                cursor: (!task.isPureWatcher && toggleDrawer) ? 'pointer' : 'default'
                              }}
                            >
                              {task.name}
                            </span>
                          </div>
                        </div>
                        <div className="text-center text-secondary" style={{ width: '151px', fontSize: '11px' }}>
                          {task.start} <span className="opacity-50 mx-1">~</span> {task.end}
                        </div>
                        <div className="text-center" style={{ width: '63px' }}>
                          {/* 지연된 경우 연한 붉은색/붉은 테두리로 경고 표시 + 상태 툴팁 적용 */}
                          <span className={`badge border cursor-help ${isDelayed ? 'bg-danger bg-opacity-10 text-danger border-danger' :
                            task.progress === 100 ? 'bg-success bg-opacity-10 text-success border-success' :
                              task.progress > 0 ? 'bg-primary bg-opacity-10 text-primary border-primary' :
                                'bg-warning bg-opacity-10 text-warning border-warning border-opacity-50'
                            }`} title={badgeTooltip}>
                            {task.progress}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* ★ 우측 달력의 하단 가로 스크롤바 두께(8px)로 인한 좌우 행 높이 어긋남을 교정하는 보정 공간 ★ */}
              <div style={{ height: '8px', flexShrink: 0 }}></div>
            </div>
          </div>

          {/* 4. 우측 달력 바디 및 화살표 오버레이 래퍼 */}
          <div className="flex-grow-1 position-relative d-flex flex-column" style={{ minWidth: 0 }}>
            {/* 가로/세로 스크롤 활성화 바디 */}
            <div className="flex-grow-1 overflow-auto custom-scrollbar bg-white" onClick={() => setTooltipData(null)} ref={bodyRightRef} onScroll={handleBodyRightScroll} style={{ minWidth: 0 }}>
              {/* 대표님 솔루션: div를 하나 더 감싸서 삐져나온 막대기로 인한 스크롤 영역 팽창을 물리적으로 잘라버림 */}
              <div style={{ width: `${canvasWidth}px`, overflow: 'hidden' }}>
                <div className="position-relative" style={{ width: `${canvasWidth}px` }}>

                  {/* Today Marker (오늘 날짜 세로 점선) */}
                  {isTodayInRange && (
                    <div className="position-absolute pointer-events-none"
                      style={{
                        left: `${todayOffset + (DAY_WIDTH / 2)}px`,
                        top: 0,
                        bottom: 0,
                        width: '0',
                        borderLeft: '2px dashed rgba(253, 126, 20, 0.7)',
                        zIndex: 15,
                        transform: 'translateX(-1px)'
                      }}>
                    </div>
                  )}

                  {/* 배경 세로 격자선 */}
                  <div className="position-absolute top-0 bottom-0 d-flex pointer-events-none" style={{ left: 0, right: 0, zIndex: 0 }}>
                    {daysArray.map((day, idx) => {
                      const isLast = idx === daysArray.length - 1; // 마지막 격자 여부
                      const isBoundary = isLast || (idx < daysArray.length - 1 && daysArray[idx + 1].getDate() === 1);
                      return (
                        <div key={idx} className={`border-end border-secondary ${isBoundary ? 'border-opacity-50' : 'border-opacity-10'}`} style={{ width: `${DAY_WIDTH}px`, backgroundColor: (day.getDay() === 0 || day.getDay() === 6) ? '#f8f9fa' : 'transparent', boxSizing: 'border-box' }}></div>
                      );
                    })}
                  </div>

                  {tasksData.map((project, index) => (
                    <React.Fragment key={project.id}>
                      {/* 프로젝트 막대 Row */}
                      <div className={`position-relative d-flex align-items-center border-bottom bg-light bg-opacity-50 hover-bg-light ${index !== 0 ? 'border-top' : ''}`} onClick={() => toggleProject(project.id)} style={{ height: `${ROW_HEIGHT}px`, boxSizing: 'border-box', zIndex: 1, borderColor: 'rgba(108, 117, 125, 0.2)', cursor: 'pointer' }}>
                        {(() => {
                          const pos = getPosition(project.start, project.end);
                          return (
                            <div className="position-absolute rounded-pill bg-secondary bg-opacity-25 overflow-hidden"
                              style={{ left: `${pos.left}px`, width: `${pos.width}px`, height: '8px', padding: '0 2px' }}>
                              <div className="h-100 bg-secondary"
                                style={{
                                  width: `${project.progress}%`,
                                  borderTopLeftRadius: '50rem',
                                  borderBottomLeftRadius: '50rem',
                                  borderTopRightRadius: project.progress === 100 ? '50rem' : '0',
                                  borderBottomRightRadius: project.progress === 100 ? '50rem' : '0'
                                }}>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      {/* 태스크 막대 Row */}
                      {expandedProjects[project.id] && project.tasks.map(task => {
                        const pos = getPosition(task.start, task.end);
                        const colors = getCapsuleColor(task.status);

                        // 대략적인 텍스트 폭 계산 (글자당 11px)
                        const textWidth = task.name.length * 11;
                        const requiredSpace = textWidth + 20;

                        const availableRight = Math.max(0, canvasWidth - (pos.left + pos.width) - 16);
                        const availableLeft = Math.max(0, pos.left - 16);

                        // 1차 판단: 캡슐 폭이 충분하면 안에 넣음
                        let isTextInside = pos.width > requiredSpace;

                        // [예외 처리] 대표님 피드백 반영: 밖으로 빼야 하는데, 좌우 바깥에 최소한의 여유 공간(40px)조차 없다면 (막대가 전체 기간을 꽉 채운 경우)
                        // 어쩔 수 없이 무조건 캡슐 안으로 강제로 욱여넣고 자른다!
                        if (!isTextInside && availableRight < 40 && availableLeft < 40) {
                          isTextInside = true;
                        }

                        // 바깥으로 나갈 때, 오른쪽과 왼쪽 중 어디로 갈지 결정 (오른쪽이 부족하고 왼쪽이 더 넓으면 왼쪽으로)
                        const goLeft = !isTextInside && (availableRight < requiredSpace) && (availableLeft > availableRight);
                        const maxOuterWidth = goLeft ? availableLeft : availableRight;

                        // [마감 지연 상태 판별 로직 추가] (다른 기능은 절대 건드리지 않음)
                        // 마감일(task.end)이 어제 이전이면서 진척도가 100%가 아닌 경우 (today는 00:00:00으로 세팅되어 있음)
                        const taskEndDate = new Date(task.end);
                        const isDelayed = taskEndDate < today && task.progress < 100;

                        // 지연된 경우 붉은색 경고 테마, 정상인 경우 기존 노란색 계열 유지
                        const capsuleBgColor = isDelayed ? '#ffe3e3' : colors.bg;
                        const capsuleFillColor = isDelayed ? '#fa5252' : colors.fill;

                        // 순수 참조자 판별 로직 (convertedData에서 이미 계산된 값 재사용)
                        const originalTaskObj = propTasks.find(t => String(t.id) === String(task.dbId)) || task;
                        const isPureWatcher = task.isPureWatcher;
                        const canEditSchedule = task.canEditSchedule;

                        return (
                          <div key={task.id} className="task-row position-relative d-flex align-items-center border-bottom border-secondary border-opacity-10 hover-bg-light transition-all" style={{ height: `${ROW_HEIGHT}px`, boxSizing: 'border-box', zIndex: 1 }}>
                            {/* 캡슐과 이벤트를 완벽히 분리하기 위해 캡슐 밖(Row 직속)으로 꺼낸 가려짐 화살표 */}
                            <div className="left-arrow position-absolute align-items-center justify-content-center rounded-circle bg-white shadow-sm border"
                              style={{ width: '18px', height: '18px', left: 0, zIndex: 10, display: 'none', cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); if (bodyRightRef.current) bodyRightRef.current.scrollTo({ left: Math.max(0, pos.left - 50), behavior: 'smooth' }); }}>
                              <i className="fas fa-caret-left text-secondary" style={{ fontSize: '12px' }}></i>
                            </div>

                            <div className="right-arrow position-absolute align-items-center justify-content-center rounded-circle bg-white shadow-sm border"
                              style={{ width: '18px', height: '18px', left: 0, zIndex: 10, display: 'none', cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); if (bodyRightRef.current) bodyRightRef.current.scrollTo({ left: Math.max(0, pos.left + pos.width - bodyRightRef.current.clientWidth + 50), behavior: 'smooth' }); }}>
                              <i className="fas fa-caret-right text-secondary" style={{ fontSize: '12px' }}></i>
                            </div>

                            {/* 태스크 캡슐 */}
                            <div
                              className={`task-capsule position-absolute rounded-pill shadow-sm d-flex align-items-center ${isPureWatcher ? '' : 'cursor-pointer'} ${isDelayed ? 'border border-danger border-opacity-50' : ''}`}
                              data-left={pos.left}
                              data-width={pos.width}
                              style={{ left: `${pos.left}px`, width: `${pos.width}px`, height: '22px', backgroundColor: capsuleBgColor, padding: '0 8px', cursor: isPureWatcher ? 'default' : 'pointer' }}
                              onMouseDown={(e) => {
                                if (canEditSchedule) handleTaskMouseDown(e, project.id, task.id, 'move');
                              }}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                if (isPureWatcher) return;
                                clearTimeout(clickTimeout.current); // 더블클릭 발생 시 일반 클릭(툴팁) 로직 취소
                                if (toggleDrawer) {
                                  // 타임라인용으로 축소된 task 객체 대신, 체크리스트/코멘트가 모두 포함된 원본 데이터를 찾아서 넘김
                                  toggleDrawer(originalTaskObj);
                                  setTooltipData(null); // 패널이 열리면 툴팁 모달은 닫아줌
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isPureWatcher) return;
                                if (dragInfo.current.hasChanged) return; // 드래그 중이었다면 무시

                                // [중요] 팝업(승인 대기 창)이 떠있을 때는 정보 레이어 클릭 이벤트 차단
                                if (inlineConfirmData) return;

                                clearTimeout(clickTimeout.current);

                                // React의 e.currentTarget은 비동기 콜백(setTimeout) 내부에서 null이 되므로, 동기적으로 미리 값을 추출합니다.
                                const rect = e.currentTarget.getBoundingClientRect();

                                clickTimeout.current = setTimeout(() => {
                                  const tooltipWidth = 240;
                                  const tooltipHeight = 120;
                                  const gap = 10;

                                  let left = rect.right + gap;
                                  let top = rect.top + rect.height / 2 - tooltipHeight / 2;

                                  // 우측 화면 밖으로 나갈 경우 좌측으로 넘김
                                  if (left + tooltipWidth > window.innerWidth) {
                                    left = rect.left - tooltipWidth - gap;
                                  }

                                  // 상단(헤더) 잘림 방지: 위로 넘어가면 막대 아래로 배치
                                  if (top < 150) {
                                    top = rect.bottom + gap;
                                    left = rect.left; // 막대 왼쪽 라인에 맞춤
                                  }
                                  // 하단 잘림 방지: 아래로 넘어가면 막대 위로 배치
                                  else if (top + tooltipHeight > window.innerHeight) {
                                    top = rect.top - tooltipHeight - gap;
                                    left = rect.left;
                                  }

                                  setTooltipData(prev => (prev && prev.taskId === task.id) ? null : { taskId: task.id, left, top, task, isDelayed });
                                }, 200); // 0.2초 지연 (더블클릭 판정 시간 대기)
                              }}
                            >

                              {/* 1. 바탕 어두운 텍스트 (연한 배경용) */}
                              {isTextInside && (
                                <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center text-dark fw-bold px-2"
                                  style={{ left: 0, top: 0, pointerEvents: 'none', fontSize: '11px', opacity: 0.8, zIndex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={task.name}>
                                  {task.name}
                                </div>
                              )}

                              {/* 2. 진척도(Fill) 영역 (진한 배경) */}
                              <div className="h-100 rounded-pill position-relative overflow-hidden" style={{ width: `${task.progress}%`, backgroundColor: capsuleFillColor, minWidth: task.progress > 0 ? '6px' : '0', zIndex: 2 }}>
                                {/* 3. 진척도 내부 하얀 텍스트 (캡슐 전체 가용너비로 고정하여 바탕 글씨와 100% 위치 일치시킴) */}
                                {isTextInside && (
                                  <div className="position-absolute h-100 d-flex align-items-center justify-content-center text-white fw-bold px-2"
                                    style={{ left: 0, top: 0, width: `${pos.width - 4}px`, pointerEvents: 'none', fontSize: '11px', textShadow: '0px 1px 2px rgba(0,0,0,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={task.name}>
                                    {task.name}
                                  </div>
                                )}
                                {/* 진척도 조절 핸들 (우측 끝) */}
                                {canEditSchedule && (
                                  <div
                                    className="position-absolute top-0 bottom-0 d-flex align-items-center justify-content-center"
                                    style={{ right: 0, width: '14px', cursor: 'col-resize', zIndex: 10, backgroundColor: 'rgba(255,255,255,0.2)', borderLeft: '1px solid rgba(255,255,255,0.4)' }}
                                    onMouseDown={(e) => handleTaskMouseDown(e, project.id, task.id, 'progress')}
                                    onClick={(e) => e.stopPropagation()}
                                    title="진척도 조절"
                                  >
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                      <div style={{ width: '1px', height: '8px', backgroundColor: 'rgba(255,255,255,0.8)' }}></div>
                                      <div style={{ width: '1px', height: '8px', backgroundColor: 'rgba(255,255,255,0.8)' }}></div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* 기간(시작일) 조절 핸들 (캡슐 좌측 끝) */}
                              {canEditSchedule && (
                                <div
                                  className="position-absolute top-0 bottom-0 d-flex align-items-center justify-content-start"
                                  style={{ left: '-4px', width: '12px', cursor: 'w-resize', zIndex: 11 }}
                                  onMouseDown={(e) => handleTaskMouseDown(e, project.id, task.id, 'resize-left')}
                                  onClick={(e) => e.stopPropagation()}
                                  title="시작일 변경"
                                />
                              )}

                              {/* 기간(종료일) 연장 핸들 (캡슐 우측 끝) */}
                              {canEditSchedule && (
                                <div
                                  className="position-absolute top-0 bottom-0 d-flex align-items-center justify-content-end"
                                  style={{ right: '-4px', width: '12px', cursor: 'e-resize', zIndex: 5 }}
                                  onMouseDown={(e) => handleTaskMouseDown(e, project.id, task.id, 'resize-right')}
                                  onClick={(e) => e.stopPropagation()}
                                  title="기간 연장"
                                />
                              )}

                            </div>

                            {/* 캡슐 바깥 텍스트 (공간이 좁을 때) - 공간 동적 계산 및 말줄임표 처리 */}
                            {!isTextInside && (
                              goLeft ? (
                                <div className="position-absolute text-secondary fw-medium text-end"
                                  style={{ right: `${canvasWidth - pos.left + 8}px`, pointerEvents: 'none', zIndex: 5, whiteSpace: 'nowrap', fontSize: '11px', maxWidth: `${maxOuterWidth}px`, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  title={task.name}>
                                  {task.name}
                                </div>
                              ) : (
                                <div className="position-absolute text-secondary fw-medium"
                                  style={{ left: `${pos.left + pos.width + 8}px`, pointerEvents: 'none', zIndex: 5, whiteSpace: 'nowrap', fontSize: '11px', maxWidth: `${maxOuterWidth}px`, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  title={task.name}>
                                  {task.name}
                                </div>
                              )
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>

              </div>
            </div>
          </div>
        </div>

      </div>

      {/* 5. 오늘로 이동 (Jump to Today) 플로팅 버튼 */}
      {isTodayInRange && showTodayButton && (
        <button
          className="btn btn-sm btn-white border-secondary border-opacity-25 shadow rounded-pill px-3 py-2 d-flex align-items-center gap-2 transition-all hover-shadow"
          onClick={goToToday}
          style={{ position: 'absolute', right: '30px', bottom: '30px', zIndex: 900, color: '#fd7e14', fontWeight: 'bold', backgroundColor: '#fff' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          title="오늘 날짜로 이동"
        >
          <i className="far fa-calendar-check fs-6"></i> 오늘
        </button>
      )}


      {/* 스크롤 제약을 뚫기 위한 최상위 CSS 커스텀 팝업 렌더링 */}
      {popupData && (
        <div
          className="expanded-popup bg-white border border-secondary border-opacity-25 shadow rounded px-2"
          style={{
            position: 'fixed',
            top: `${popupData.top + (popupData.height / 2)}px`,
            left: `${popupData.left + 8}px`,
            transform: 'translateY(-50%)',
            zIndex: 9999,
            whiteSpace: 'nowrap',
            fontSize: '13px',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 8px'
          }}
        >
          <span className="fw-bolder" style={{ color: '#845ef7' }}>{popupData.text}</span>
        </div>
      )}

      {/* 스마트 포지셔닝 툴팁 (Portal을 통해 document.body에 직접 렌더링하여 잘림 완벽 방지) */}
      {tooltipData && createPortal(
        <div
          className="position-fixed bg-dark text-white rounded-3 shadow-lg px-3 py-3 animation-fade-in"
          style={{
            left: `${tooltipData.left}px`,
            top: `${tooltipData.top}px`,
            width: '240px',
            zIndex: 99999
          }}
        >
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="fw-bolder text-info fs-7 text-truncate pe-2">
              {tooltipData.isDelayed && <span className="badge bg-danger me-2">지연</span>}
              {tooltipData.task.name}
            </div>
            <i className="fas fa-times text-secondary hover-text-white" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setTooltipData(null); }} title="닫기"></i>
          </div>
          <div className="opacity-75 mb-1 fs-8"><i className="far fa-calendar-alt me-2"></i>{tooltipData.task.start} ~ {tooltipData.task.end}</div>
          <div className="opacity-75 mb-1 fs-8"><i className="fas fa-tasks me-2"></i>진척도: <span className="text-white fw-bold">{tooltipData.task.progress}%</span></div>
          <div className="opacity-75 fs-8"><i className="fas fa-user-circle me-2"></i>{tooltipData.task.assignee || '미배정'}</div>
        </div>,
        document.body
      )}

      <InlineConfirmPopup
        data={inlineConfirmData}
        onConfirm={handleInlineConfirmAction}
        onCancel={handleInlineCancelAction}
      />
    </div>
  );
}

export default TimelineTab;
